import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Big } from "big.js";
import Parse from "parse";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { Stage } from "../../shared/CaseConfiguration";
import { AwardDue, Closing } from "../../shared/judgment";
import {
  endDateRange,
  IsoDate,
  showDate,
  withEnded,
} from "../../shared/timeline";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { amountOf, PartySpending, useStageSpending } from "./CloseStageDialog";
import { AsExpectedButton } from "./AsExpectedButton";

const button = {
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;
const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;

/**
 * Closes Enforcement, and with it the case: what was spent on the stage, and
 * what was actually paid on each award of the clients. Nothing is entered for
 * the user: an amount stays empty until they enter it or click In full.
 */
export const CloseEnforcementDialog = ({
  today,
  onClose,
}: {
  today: IsoDate;
  onClose: () => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.closingCase",
  });
  const { t: tRoot } = useTranslation();
  const { currency } = useCurrency();
  const money = (value: Big | number) =>
    currency.format(typeof value === "number" ? value : value.toNumber());
  const caseContext = useCase();
  const c = caseContext.getCase();
  const submit = useCaseSubmit();
  const stage = Stage.Enforcement;
  const stageName = tRoot(`home.stages.${Stage[stage]}`);
  const spending = useStageSpending(stage);

  const [date, setDate] = useState<IsoDate>("");
  const [awards, setAwards] = useState<AwardDue[]>();
  const [failed, setFailed] = useState(false);
  const [paid, setPaid] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    let cancelled = false;
    Parse.Cloud.run("getJudgmentAwards", { caseId: c.id })
      .then((due: AwardDue[]) => {
        if (!cancelled) setAwards(due);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("Failed to load the awards of the judgment", e);
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [c.id]);

  const clients = new Set(
    (c.parties ?? []).filter(({ client }) => client).map(({ id }) => id),
  );
  const partyName = (id: string) =>
    c.parties?.find((party) => party.id === id)?.name ?? id;
  const rows = (awards ?? []).filter(
    ({ payerId, recipientId }) =>
      clients.has(payerId) || clients.has(recipientId),
  );
  const paidOf = ({ key }: AwardDue) => amountOf(paid[key] ?? "");
  const range = endDateRange(c.timeline, caseContext.getStages(), stage, today);
  const tooEarly = date !== "" && range.min !== undefined && date < range.min;
  const validDate = date !== "" && !tooEarly && date <= range.max;
  const allPaid = rows.every((row) => paidOf(row) !== undefined);
  const missing = [
    !validDate && t("parts.date"),
    !spending.validAmounts && t("parts.costs"),
    !allPaid && t("parts.paid"),
  ].filter((part): part is string => !!part);

  const close = () => {
    const closing: Closing = {
      payments: rows.map(({ key, payerId, recipientId, ...row }) => ({
        key,
        payerId,
        recipientId,
        amount: paidOf({ key, payerId, recipientId, ...row })!.toNumber(),
      })),
    };
    const budgets = spending.changedBudgets();
    return Promise.resolve(
      submit(
        {
          timeline: JSON.stringify(withEnded(c.timeline, stage, date)),
          budgets: JSON.stringify(budgets),
          closing: JSON.stringify(closing),
        },
        { method: "put" },
      ),
    ).then(onClose);
  };

  const sum = (of: (row: AwardDue) => boolean) =>
    rows
      .filter(of)
      .reduce((total, row) => total.add(paidOf(row) ?? 0), new Big(0));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("title")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("description")}</DialogContentText>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <TextField
            type="date"
            size="small"
            required
            label={t("date")}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            error={date !== "" && !validDate}
            helperText={
              date === "" || validDate
                ? undefined
                : tooEarly
                  ? tRoot("case.timeline.notBefore", {
                      date: showDate(range.min!),
                    })
                  : tRoot("case.timeline.notAfter", {
                      date: showDate(range.max),
                    })
            }
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: range.min, max: range.max },
            }}
            sx={{ width: 220 }}
          />
          <Button variant="outlined" sx={button} onClick={() => setDate(today)}>
            {t("today")}
          </Button>
          <AsExpectedButton stage={stage} today={today} onPick={setDate} />
        </Stack>

        {spending.parties.map((party) => (
          <PartySpending
            key={party.partyId}
            party={party}
            stageName={stageName}
            final
            textOf={(type) => spending.textOf(party.partyId, type)}
            set={(type, value) => spending.set(party.partyId, type, value)}
          />
        ))}

        <Box sx={{ mt: 3 }}>
          <SectionLabel component="h3">{t("awards.title")}</SectionLabel>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {t("awards.description")}
          </Typography>
          {failed && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t("awards.failed")}
            </Alert>
          )}
          {awards && (
            <Box sx={{ overflowX: "auto", mt: 1 }}>
              <Table size="small" sx={{ tableLayout: "fixed", minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "32%" }}>
                      {t("awards.column.award")}
                    </TableCell>
                    <TableCell align="right" sx={{ width: "16%" }}>
                      {t("awards.column.awarded")}
                    </TableCell>
                    <TableCell sx={{ width: "28%" }}>
                      {t("awards.column.paid")}
                    </TableCell>
                    <TableCell sx={{ width: "24%" }}>
                      {t("awards.column.then")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const value = paidOf(row);
                    const short = value && new Big(row.amount).minus(value);
                    const toClient = clients.has(row.recipientId);
                    return (
                      <TableRow key={row.key}>
                        <TableCell component="th" scope="row">
                          {row.damage ?? t("awards.costRecovery")}
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{ color: "text.secondary" }}
                          >
                            {t("awards.pays", {
                              payer: partyName(row.payerId),
                              recipient: partyName(row.recipientId),
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={amount}>
                          {money(row.amount)}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <TextField
                              size="small"
                              value={paid[row.key] ?? ""}
                              onChange={(event) =>
                                setPaid((was) => ({
                                  ...was,
                                  [row.key]: event.target.value,
                                }))
                              }
                              slotProps={{
                                htmlInput: {
                                  inputMode: "decimal",
                                  "aria-label": t("awards.paidOn", {
                                    name:
                                      row.damage ?? t("awards.costRecovery"),
                                  }),
                                  style: { textAlign: "right" },
                                },
                              }}
                              sx={{ width: 120 }}
                            />
                            <Button
                              size="small"
                              sx={button}
                              onClick={() =>
                                setPaid((was) => ({
                                  ...was,
                                  [row.key]: row.amount.toString(),
                                }))
                              }
                            >
                              {t("awards.inFull")}
                            </Button>
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 13,
                            color: short?.eq(0)
                              ? "text.secondary"
                              : short
                                ? "warning.dark"
                                : "text.secondary",
                            fontWeight: short && !short.eq(0) ? 600 : undefined,
                          }}
                        >
                          {short === undefined
                            ? t("awards.toEnter")
                            : short.eq(0)
                              ? t("awards.paidInFull")
                              : short.lt(0)
                                ? t("awards.more", {
                                    amount: money(short.abs()),
                                  })
                                : t(
                                    toClient
                                      ? "awards.notRecovered"
                                      : "awards.notPaid",
                                    { amount: money(short) },
                                  )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {spending.parties.map(({ partyId, name }) => {
          const paidOut = sum(({ payerId }) => payerId === partyId);
          const received = sum(({ recipientId }) => recipientId === partyId);
          const tiles: [string, Big][] = [
            [t("summary.paid"), paidOut],
            [t("summary.received"), received],
            [t("summary.net"), received.minus(paidOut)],
          ];
          return (
            <Box
              key={partyId}
              sx={{ mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}
            >
              <SectionLabel component="h3">
                {t("summary.title", { name })}
              </SectionLabel>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 2,
                  mt: 1,
                }}
              >
                {tiles.map(([label, value]) => (
                  <Box key={label}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 600, ...amount }}
                    >
                      {money(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          {t("reopenHint")}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {missing.length > 0 && (
          <Typography
            variant="body2"
            sx={{ mr: "auto", color: "text.secondary" }}
          >
            {t("missing", { parts: missing.join(", ") })}
          </Typography>
        )}
        <Button sx={button} onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={button}
          disabled={missing.length > 0 || awards === undefined}
          onClick={close}
        >
          {t("submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
